import { useRef, useState } from 'react';
import * as orgApi from '../api/organizations';
import { useToast } from './toast';
import { Field, SelectField, TextareaField } from './ui';
import { cn, ui } from '../lib/ui';
import {
  PROFILE_COPY,
  PROFILE_FIELDS,
  US_STATES,
  orgTypePolicy,
  suggestedDonorDisclosure,
  validateProfile,
} from '../lib/orgTypes';
import { fieldErrorsFrom, focusFirstInvalid } from '../lib/formErrors';

const STATUS_NOTE = {
  draft: { pill: ui.pillNeutral, label: 'Draft', text: 'Saved but not submitted for review.' },
  submitted: {
    pill: ui.pillInfo,
    label: 'Waiting for review',
    text: 'The platform team reviews submissions, usually within two business days.',
  },
  verified: {
    pill: ui.pillSuccess,
    label: 'Verified',
    text: 'The platform team verified this profile.',
  },
  returned: {
    pill: ui.pillWarning,
    label: 'Returned',
    text: 'The platform team asked for changes.',
  },
};

const UNLOCKS = {
  nonprofit: 'Donations open once the platform verifies this profile.',
  investment: 'Member services open once the platform verifies this profile.',
};

function initialProfile(org, profileType) {
  const stored = org.compliance?.profileType === profileType ? org.compliance.profile : null;
  if (stored) return { ...stored };
  return profileType === 'nonprofit' ? { solicitsInCalifornia: true } : {};
}

/**
 * The compliance profile of a nonprofit or investment organization
 * (docs/Decisions.md D64): save a draft at any time, submit with an
 * attestation for the platform to verify. Errors from this form or the API
 * sit beside their fields. Used by the setup wizard and Org settings.
 *
 * Props: org (with compliance, as org admins see it); onSaved(org);
 * renderActions({ busy, saveDraft, submit }) for the buttons.
 */
export default function ComplianceProfileForm({ org, onSaved, renderActions }) {
  const toast = useToast();
  const rootRef = useRef(null);
  const policy = orgTypePolicy(org.orgType);
  const profileType = policy?.profile;
  const [profile, setProfile] = useState(() => initialProfile(org, profileType));
  const [attest, setAttest] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(null);

  if (!profileType) return null;
  const fields = PROFILE_FIELDS[profileType];
  const copy = PROFILE_COPY[profileType];
  const compliance = org.compliance?.profileType === profileType ? org.compliance : null;
  const status = compliance?.status || null;

  function set(key, value) {
    setProfile(prev => ({ ...prev, [key]: value }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function cleaned() {
    return Object.fromEntries(
      Object.entries(profile).filter(([, v]) => v !== '' && v !== undefined && v !== null)
    );
  }

  async function send(submit) {
    const body = cleaned();
    const { fieldErrors } = validateProfile(org.orgType, body, { submit });
    if (submit && !attest) {
      fieldErrors.attest = 'Confirm that the information is accurate and that you may submit it';
    }
    if (Object.keys(fieldErrors).length) {
      setErrors(fieldErrors);
      focusFirstInvalid(rootRef.current);
      toast.error('Some fields need attention - see the highlighted fields');
      return false;
    }
    setBusy(submit ? 'submit' : 'draft');
    try {
      const data = await orgApi.updateOrganization(org.orgId, {
        compliance: { profile: body, submit, attest: submit ? attest : undefined },
      });
      setErrors({});
      toast.success(submit ? 'Profile submitted for review' : 'Draft saved');
      onSaved?.(data.organization);
      return true;
    } catch (err) {
      const found = fieldErrorsFrom(err, 'compliance');
      if (Object.keys(found).length) {
        setErrors(found);
        focusFirstInvalid(rootRef.current);
      }
      toast.error(err.message || 'Failed to save the profile');
      return false;
    } finally {
      setBusy(null);
    }
  }

  function renderField(key, def) {
    if (def.requiredWhen && !def.requiredWhen(profile)) return null;
    const c = copy[key];
    const required = def.required || Boolean(def.requiredWhen);
    const label = required ? c.label : `${c.label} (optional)`;
    const common = { label, hint: c.hint, error: errors[key] };
    switch (def.kind) {
      case 'choice':
        return (
          <SelectField
            key={key}
            {...common}
            value={profile[key] || ''}
            onChange={e => set(key, e.target.value)}
          >
            <option value="">Choose...</option>
            {Object.entries(def.options).map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </SelectField>
        );
      case 'state':
        return (
          <SelectField
            key={key}
            {...common}
            value={profile[key] || ''}
            onChange={e => set(key, e.target.value)}
          >
            <option value="">Choose...</option>
            {US_STATES.map(code => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </SelectField>
        );
      case 'boolean':
      case 'accept': {
        const id = `profile-${key}`;
        return (
          <div className="mb-4" key={key}>
            <label className="flex items-start gap-3" htmlFor={id}>
              <input
                id={id}
                type="checkbox"
                className={cn(ui.checkbox, 'mt-1 shrink-0')}
                checked={profile[key] === true}
                onChange={e => set(key, e.target.checked)}
                aria-invalid={errors[key] ? true : undefined}
                aria-describedby={c.hint ? `${id}-hint` : undefined}
              />
              <span className="text-body-s">{c.label}</span>
            </label>
            {c.hint && (
              <span className={cn(ui.hint, 'ml-7 block')} id={`${id}-hint`}>
                {c.hint}
              </span>
            )}
            {errors[key] && (
              <span className={cn(ui.fieldError, 'ml-7')} role="alert">
                {errors[key]}
              </span>
            )}
          </div>
        );
      }
      case 'text':
        if (def.max > 300) {
          return (
            <div key={key}>
              <TextareaField
                {...common}
                rows={4}
                maxLength={def.max}
                value={profile[key] || ''}
                onChange={e => set(key, e.target.value)}
              />
              {key === 'donorDisclosure' && (
                <button
                  type="button"
                  className={cn(ui.btnGhost, ui.btnSm, '-mt-2 mb-4')}
                  onClick={() => set(key, suggestedDonorDisclosure(profile))}
                >
                  Fill in a suggested statement
                </button>
              )}
            </div>
          );
        }
        return (
          <Field
            key={key}
            {...common}
            maxLength={def.max}
            value={profile[key] || ''}
            onChange={e => set(key, e.target.value)}
          />
        );
      default:
        return (
          <Field
            key={key}
            {...common}
            type={def.kind === 'email' ? 'email' : 'text'}
            placeholder={
              def.kind === 'ein'
                ? '12-3456789'
                : def.kind === 'caRegistry'
                  ? 'CT0123456'
                  : undefined
            }
            value={profile[key] || ''}
            onChange={e => set(key, e.target.value)}
          />
        );
    }
  }

  const note = status ? STATUS_NOTE[status] : null;
  return (
    <div ref={rootRef}>
      {note && (
        <div className={cn(ui.row, 'mb-3 items-center')}>
          <span className={note.pill}>{note.label}</span>
          <span className={cn(ui.muted, 'text-body-s')}>{note.text}</span>
        </div>
      )}
      {status === 'returned' && compliance?.reviewNote && (
        <div className={cn(ui.alertWarning, 'mb-4')}>
          <strong>What to change:</strong> {compliance.reviewNote}
        </div>
      )}
      {compliance?.verified && status !== 'verified' && (
        <p className={cn(ui.alertInfo, 'mb-4')}>
          Your verified profile stays in force until the platform team reviews your changes.
        </p>
      )}
      {!compliance?.verified && (
        <p className={cn(ui.muted, 'text-body-s mb-4')}>{UNLOCKS[profileType]}</p>
      )}

      {Object.entries(fields).map(([key, def]) => renderField(key, def))}

      <div className="mb-4 rounded-z-card border border-z-line p-3">
        <label className="flex items-start gap-3" htmlFor="profile-attest">
          <input
            id="profile-attest"
            type="checkbox"
            className={cn(ui.checkbox, 'mt-1 shrink-0')}
            checked={attest}
            onChange={e => {
              setAttest(e.target.checked);
              setErrors(prev => ({ ...prev, attest: undefined }));
            }}
            aria-invalid={errors.attest ? true : undefined}
          />
          <span className="text-body-s">
            I confirm this information is accurate and that I am authorized to submit it for{' '}
            {org.name}. I will update it if anything changes.
          </span>
        </label>
        {errors.attest && (
          <span className={cn(ui.fieldError, 'ml-7')} role="alert">
            {errors.attest}
          </span>
        )}
      </div>

      {renderActions ? (
        renderActions({
          busy,
          saveDraft: () => send(false),
          submit: () => send(true),
          canSaveDraft: status !== 'submitted',
        })
      ) : (
        <div className={cn(ui.row, 'mt-2')}>
          <button
            type="button"
            className={ui.btnPrimary}
            onClick={() => send(true)}
            disabled={Boolean(busy)}
          >
            {busy === 'submit' ? 'Submitting...' : 'Submit for review'}
          </button>
          {status !== 'submitted' && (
            <button
              type="button"
              className={ui.btnSecondary}
              onClick={() => send(false)}
              disabled={Boolean(busy)}
            >
              {busy === 'draft' ? 'Saving...' : 'Save draft'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
